import styled from '@emotion/styled';
import { uiTheme as t } from './theme';

export interface TabOption<T extends string> {
  value: T;
  label: string;
  count: number;
}

interface SegmentedTabsProps<T extends string> {
  options: TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

/**
 * Carril y celda del control segmentado, como objetos sueltos.
 *
 * Se exportan porque `MetricPill` se coloca al lado y tiene que medir
 * exactamente igual. Compartir el objeto es lo único que garantiza que sigan
 * midiendo igual: copiando los valores, el día que alguien cambie el relleno
 * aquí la fila se desalinea y ninguna prueba se entera.
 *
 * `lineHeight: 'normal'` está puesto a mano porque la celda del control es un
 * `<button>`, que no hereda el `line-height: 1.5` que `CssBaseline` pone en el
 * `body`. Sin fijarlo, cualquier celda que no sea un botón sale más alta.
 */
export const segmentTrackStyle = {
  display: 'inline-flex' as const,
  alignItems: 'center' as const,
  gap: 4,
  padding: 4,
  borderRadius: 11,
  background: t.color.segmentTrack,
};

export const segmentItemStyle = {
  display: 'flex' as const,
  alignItems: 'center' as const,
  gap: 7,
  padding: '8px 16px',
  border: 'none',
  margin: 0,
  borderRadius: t.radius.xs,
  fontFamily: t.font,
  fontSize: 13.5,
  lineHeight: 'normal',
};

export const segmentCountStyle = {
  fontFamily: t.font,
  fontSize: 11.5,
  padding: '1px 8px',
  borderRadius: 10,
  background: '#e2e6ea',
  color: '#6b7178',
};

const Track = styled.div(segmentTrackStyle);

const Tab = styled.button<{ active: boolean }>(({ active }) => ({
  ...segmentItemStyle,
  cursor: 'pointer',
  fontWeight: active ? 600 : 500,
  color: active ? t.color.segmentActiveText : t.color.text,
  background: active ? t.color.surface : 'transparent',
  boxShadow: active ? '0 1px 2px rgba(16, 24, 40, 0.12)' : 'none',
  transition: 'background 0.15s ease, color 0.15s ease',
}));

const Count = styled.span(segmentCountStyle);

/**
 * Molécula: control segmentado con conteo por opción (Todos / Internos / ...).
 * Genérico sobre el tipo del valor para reutilizarse con cualquier enum.
 */
function SegmentedTabs<T extends string>({ options, value, onChange }: SegmentedTabsProps<T>) {
  return (
    <Track>
      {options.map(option => (
        <Tab key={option.value} type="button" active={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
          <Count>{option.count}</Count>
        </Tab>
      ))}
    </Track>
  );
}

export default SegmentedTabs;
